import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calculator,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  PiggyBank,
  Home,
  Heart,
  Briefcase,
  Info,
  ChevronRight,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { API_BASE_URL, API_URL } from "../utils/api";

const BACKEND_URL = API_BASE_URL;
const API = API_URL;

const CalculatorPage = () => {
  const navigate = useNavigate();
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    financial_year: "FY2025-26",
    // 5 Heads of Income
    salary_income: "",
    house_property_income: "",
    business_income: "",
    // Capital gains split (special-rate)
    stcg_equity: "",
    ltcg_equity: "",
    ltcg_other: "",
    capital_gains: "",          // slab-rate residual (debt STCG etc.)
    other_sources_income: "",
    // HRA exemption inputs
    basic_salary: "",
    hra_received: "",
    rent_paid: "",
    is_metro: true,
    // Deductions
    section_80c: "",
    section_80d_self: "",
    section_80d_parents: "",
    home_loan_interest: "",
    nps_80ccd: "",                  // Employee voluntary 80CCD(1B), Old regime only
    nps_employer_80ccd2: "",        // Employer NPS 80CCD(2) — both regimes
    other_deductions: "",
    // Demographics
    age: "below_60",
    residency_status: "resident"
  });

  // FY metadata for context badge + metro city list
  const FY_INFO = {
    "FY2024-25": {
      label: "AY 2025-26 / PY 2024-25",
      act: "Income Tax Act, 1961",
      pillColor: "amber",
      metroCount: 4,
      metroCities: "Delhi, Mumbai, Chennai, Kolkata",
    },
    "FY2025-26": {
      label: "AY 2026-27 / PY 2025-26",
      act: "Income Tax Act, 1961",
      pillColor: "amber",
      metroCount: 4,
      metroCities: "Delhi, Mumbai, Chennai, Kolkata",
    },
    "FY2026-27": {
      label: "Tax Year 2026-27",
      act: "Income Tax Act, 2025",
      pillColor: "emerald",
      metroCount: 8,
      metroCities: "Delhi, Mumbai, Chennai, Kolkata, Bengaluru, Hyderabad, Ahmedabad, Pune",
    },
  };
  const fyInfo = FY_INFO[formData.financial_year] || FY_INFO["FY2025-26"];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateTax = async () => {
    const totalIncome =
      (parseFloat(formData.salary_income) || 0) +
      (parseFloat(formData.house_property_income) || 0) +
      (parseFloat(formData.business_income) || 0) +
      (parseFloat(formData.stcg_equity) || 0) +
      (parseFloat(formData.ltcg_equity) || 0) +
      (parseFloat(formData.ltcg_other) || 0) +
      (parseFloat(formData.capital_gains) || 0) +
      (parseFloat(formData.other_sources_income) || 0);
    if (totalIncome <= 0) {
      toast.error("Please enter income under at least one head");
      return;
    }

    setIsCalculating(true);
    try {
      const payload = {
        financial_year: formData.financial_year,
        salary_income: parseFloat(formData.salary_income) || 0,
        house_property_income: parseFloat(formData.house_property_income) || 0,
        business_income: parseFloat(formData.business_income) || 0,
        stcg_equity: parseFloat(formData.stcg_equity) || 0,
        ltcg_equity: parseFloat(formData.ltcg_equity) || 0,
        ltcg_other: parseFloat(formData.ltcg_other) || 0,
        capital_gains: parseFloat(formData.capital_gains) || 0,
        other_sources_income: parseFloat(formData.other_sources_income) || 0,
        basic_salary: parseFloat(formData.basic_salary) || 0,
        hra_received: parseFloat(formData.hra_received) || 0,
        rent_paid: parseFloat(formData.rent_paid) || 0,
        is_metro: formData.is_metro,
        section_80c: parseFloat(formData.section_80c) || 0,
        section_80d_self: parseFloat(formData.section_80d_self) || 0,
        section_80d_parents: parseFloat(formData.section_80d_parents) || 0,
        home_loan_interest: parseFloat(formData.home_loan_interest) || 0,
        nps_80ccd: parseFloat(formData.nps_80ccd) || 0,
        nps_employer_80ccd2: parseFloat(formData.nps_employer_80ccd2) || 0,
        other_deductions: parseFloat(formData.other_deductions) || 0,
        age: formData.age,
        residency_status: formData.residency_status
      };

      const response = await axios.post(`${API}/calculate-tax`, payload);
      setResult(response.data);
      toast.success("Tax calculated successfully!");
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error("Failed to calculate tax. Please try again.");
    } finally {
      setIsCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-amber-500 flex items-center justify-center">
                <Calculator className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-semibold text-white">Tax Calculator</h1>
                <p className="text-xs text-slate-500">Old vs New Regime</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button 
              variant="ghost"
              onClick={() => navigate('/chat')}
              className="text-slate-400 hover:text-white"
              data-testid="ask-ai-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Ask AI
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
            Income Tax <span className="gradient-text">Calculator</span>
          </h2>
          <p className="text-slate-400">
            Compare Old vs New regime with full surcharge, rebate &amp; marginal relief calculations
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Input Form */}
          <motion.div 
            className="glass rounded-2xl p-6 lg:col-span-7"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3 className="font-heading font-semibold text-white text-lg mb-6 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-cyan-400" />
              Enter Your Details
            </h3>

            <div className="space-y-6">
              {/* STEP 1: Financial Year */}
              <div className="space-y-3 -mt-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-cyan-400">Step 1</span>
                  Select Financial Year
                </Label>
                <Select
                  value={formData.financial_year}
                  onValueChange={(v) => handleInputChange('financial_year', v)}
                >
                  <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="select-fy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FY2024-25">FY 2024-25</SelectItem>
                    <SelectItem value="FY2025-26">FY 2025-26</SelectItem>
                    <SelectItem value="FY2026-27">FY 2026-27</SelectItem>
                  </SelectContent>
                </Select>
                <div
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border ${
                    fyInfo.pillColor === 'emerald'
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-amber-500/10 border-amber-500/30'
                  }`}
                  data-testid="fy-context-badge"
                >
                  <div className="flex items-center gap-2">
                    <Info className={`w-4 h-4 ${fyInfo.pillColor === 'emerald' ? 'text-emerald-400' : 'text-amber-400'}`} />
                    <div>
                      <p className={`text-sm font-semibold ${fyInfo.pillColor === 'emerald' ? 'text-emerald-300' : 'text-amber-300'}`}>
                        {fyInfo.label}
                      </p>
                      <p className="text-[11px] text-slate-400">as per <span className="font-medium text-slate-300">{fyInfo.act}</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 2: Age + Residency */}
              <div className="space-y-4 pt-2 border-t border-slate-800">
                <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">
                  <span className="text-cyan-400 mr-1">Step 2 ·</span> Age &amp; Residential Status
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Age Category</Label>
                    <Select value={formData.age} onValueChange={(v) => handleInputChange('age', v)}>
                      <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="select-age">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="below_60">Below 60 years</SelectItem>
                        <SelectItem value="60_to_79">60 – 79 years (Senior)</SelectItem>
                        <SelectItem value="80_above">80 years &amp; above (Super Senior)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Residency Status</Label>
                    <Select value={formData.residency_status} onValueChange={(v) => handleInputChange('residency_status', v)}>
                      <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="select-residency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resident">Resident Indian</SelectItem>
                        <SelectItem value="nri">Non-Resident Indian (NRI)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.residency_status === 'nri' && (
                  <p className="text-xs text-amber-400">
                    Note: NRIs are not eligible for Section 87A rebate and have limited HRA benefits.
                  </p>
                )}
              </div>

              {/* STEP 3: Heads of Income + Exemptions/Deductions (two columns) */}
              <div className="space-y-4 pt-4 border-t border-slate-800">
                <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">
                  <span className="text-cyan-400 mr-1">Step 3 ·</span> Income &amp; Deductions
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* LEFT: 5 Heads of Income */}
                  <div className="space-y-3 p-4 rounded-xl bg-slate-900/30 border border-slate-800">
                    <p className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5" /> Heads of Income
                    </p>

                    <div className="space-y-2">
                      <Label className="text-slate-300 text-xs">Salary <span className="text-slate-500">(before std. deduction)</span></Label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        <Input
                          type="number"
                          placeholder="0"
                          value={formData.salary_income}
                          onChange={(e) => handleInputChange('salary_income', e.target.value)}
                          className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-salary-income"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300 text-xs flex items-center justify-between">
                        <span>House Property</span>
                        <span className="text-[10px] text-slate-500">negative = loss (max ₹2L set-off)</span>
                      </Label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        <Input
                          type="number"
                          placeholder="0 (or -200000 for loss)"
                          value={formData.house_property_income}
                          onChange={(e) => handleInputChange('house_property_income', e.target.value)}
                          className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-hp-income"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300 text-xs">Business / Profession</Label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        <Input
                          type="number"
                          placeholder="0"
                          value={formData.business_income}
                          onChange={(e) => handleInputChange('business_income', e.target.value)}
                          className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-business-income"
                        />
                      </div>
                    </div>

                    {/* Capital Gains — split by tax rate */}
                    <div className="space-y-2 pt-1">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">Capital Gains</p>
                      <div className="space-y-1.5">
                        <Label className="text-slate-400 text-[11px] flex items-center justify-between">
                          <span>STCG — Equity (Sec 111A)</span>
                          <span className="text-orange-400 font-medium">@ 20%</span>
                        </Label>
                        <div className="relative">
                          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <Input
                            type="number" placeholder="0"
                            value={formData.stcg_equity}
                            onChange={(e) => handleInputChange('stcg_equity', e.target.value)}
                            className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                            data-testid="input-stcg-equity"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-slate-400 text-[11px] flex items-center justify-between">
                          <span>LTCG — Equity (Sec 112A)</span>
                          <span className="text-orange-400 font-medium">@ 12.5% &gt;₹1.25L</span>
                        </Label>
                        <div className="relative">
                          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <Input
                            type="number" placeholder="0"
                            value={formData.ltcg_equity}
                            onChange={(e) => handleInputChange('ltcg_equity', e.target.value)}
                            className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                            data-testid="input-ltcg-equity"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-slate-400 text-[11px] flex items-center justify-between">
                          <span>Other LTCG (Sec 112)</span>
                          <span className="text-orange-400 font-medium">@ 12.5%</span>
                        </Label>
                        <div className="relative">
                          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <Input
                            type="number" placeholder="0"
                            value={formData.ltcg_other}
                            onChange={(e) => handleInputChange('ltcg_other', e.target.value)}
                            className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                            data-testid="input-ltcg-other"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 pt-1">
                      <Label className="text-slate-300 text-xs">Other Sources</Label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        <Input
                          type="number"
                          placeholder="Interest, dividends, etc."
                          value={formData.other_sources_income}
                          onChange={(e) => handleInputChange('other_sources_income', e.target.value)}
                          className="pl-8 h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-other-sources"
                        />
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: Exemptions & Deductions */}
                  <div className="space-y-3 p-4 rounded-xl bg-slate-900/30 border border-slate-800">
                    <p className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold flex items-center gap-1.5">
                      <PiggyBank className="w-3.5 h-3.5" /> Exemptions &amp; Deductions
                    </p>

                    {/* HRA sub-section */}
                    <div className="space-y-2 pb-2 border-b border-slate-800">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">HRA Exemption (Salary)</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-slate-400 text-[11px]">Basic Salary</Label>
                          <Input
                            type="number" placeholder="0"
                            value={formData.basic_salary}
                            onChange={(e) => handleInputChange('basic_salary', e.target.value)}
                            className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                            data-testid="input-basic-salary"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-[11px]">HRA Received</Label>
                          <Input
                            type="number" placeholder="0"
                            value={formData.hra_received}
                            onChange={(e) => handleInputChange('hra_received', e.target.value)}
                            className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                            data-testid="input-hra"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-slate-400 text-[11px]">Rent Paid (Annual)</Label>
                          <Input
                            type="number" placeholder="0"
                            value={formData.rent_paid}
                            onChange={(e) => handleInputChange('rent_paid', e.target.value)}
                            className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                            data-testid="input-rent"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Switch
                          checked={formData.is_metro}
                          onCheckedChange={(v) => handleInputChange('is_metro', v)}
                          data-testid="switch-metro"
                        />
                        <div className="flex-1">
                          <Label className="text-slate-300 text-[11px] block">
                            Metro City ({formData.is_metro ? '50% of Basic' : '40% of Basic'})
                          </Label>
                          <p className="text-[10px] text-slate-500">{fyInfo.metroCities} <span className="text-slate-600">({fyInfo.metroCount} cities)</span></p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-400 text-[11px]">Section 80C <span className="text-slate-500">(Max ₹1.5L)</span></Label>
                      <Input
                        type="number" placeholder="PPF, ELSS, LIC..."
                        value={formData.section_80c}
                        onChange={(e) => handleInputChange('section_80c', e.target.value)}
                        className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                        data-testid="input-80c"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-slate-400 text-[11px]">80D Self</Label>
                        <Input
                          type="number" placeholder="₹25-50K"
                          value={formData.section_80d_self}
                          onChange={(e) => handleInputChange('section_80d_self', e.target.value)}
                          className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-80d-self"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-[11px]">80D Parents</Label>
                        <Input
                          type="number" placeholder="₹50K"
                          value={formData.section_80d_parents}
                          onChange={(e) => handleInputChange('section_80d_parents', e.target.value)}
                          className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-80d-parents"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-slate-400 text-[11px]">Home Loan Int.</Label>
                        <Input
                          type="number" placeholder="₹2L max"
                          value={formData.home_loan_interest}
                          onChange={(e) => handleInputChange('home_loan_interest', e.target.value)}
                          className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-home-loan"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-[11px]">NPS Self 80CCD(1B)</Label>
                        <Input
                          type="number" placeholder="₹50K max · OTR only"
                          value={formData.nps_80ccd}
                          onChange={(e) => handleInputChange('nps_80ccd', e.target.value)}
                          className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                          data-testid="input-nps"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                      <Label className="text-emerald-300 text-[11px] flex items-center justify-between">
                        <span>Employer NPS 80CCD(2)</span>
                        <span className="text-[10px] text-emerald-400/70">Both regimes ✓</span>
                      </Label>
                      <Input
                        type="number" placeholder="OTR: 10% · NTR: 14% of Basic"
                        value={formData.nps_employer_80ccd2}
                        onChange={(e) => handleInputChange('nps_employer_80ccd2', e.target.value)}
                        className="h-9 bg-slate-900/50 border-emerald-500/30 text-white text-sm"
                        data-testid="input-nps-employer"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-400 text-[11px]">Other Deductions (80E, 80G, etc.)</Label>
                      <Input
                        type="number" placeholder="0"
                        value={formData.other_deductions}
                        onChange={(e) => handleInputChange('other_deductions', e.target.value)}
                        className="h-9 bg-slate-900/50 border-slate-700 text-white text-sm"
                        data-testid="input-other"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 4: Calculate Button */}
              <div className="pt-2">
                <p className="text-[11px] uppercase tracking-wider text-cyan-400 mb-2 font-semibold">Step 4</p>
                <Button 
                  onClick={calculateTax}
                  disabled={isCalculating}
                  className="w-full btn-primary text-white py-6 text-lg font-semibold"
                  data-testid="calculate-btn"
                >
                  {isCalculating ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner" /> Calculating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Calculator className="w-5 h-5" /> Calculate Tax
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div 
            className="space-y-6 lg:col-span-5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {result ? (
              <>
                {/* Period Header */}
                <div className="glass rounded-xl px-4 py-3 flex items-center justify-between" data-testid="result-period-header">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-slate-300">
                      Computed for <span className="font-semibold text-white">{result.period_label}</span>
                    </span>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                    {result.act_reference}
                  </span>
                </div>

                {/* Recommendation Card */}
                <div className={`glass rounded-2xl p-6 border ${
                  result.better_regime === "New Regime" 
                    ? 'border-cyan-500/50' 
                    : result.better_regime === "Old Regime"
                    ? 'border-amber-500/50'
                    : 'border-slate-700'
                }`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      result.better_regime === "New Regime" 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {result.savings > 0 ? <TrendingDown className="w-6 h-6" /> : <TrendingUp className="w-6 h-6" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-white text-lg">
                        {result.better_regime} is Better!
                      </h3>
                      <p className="text-slate-400 mt-1">{result.recommendation}</p>
                      {result.savings > 0 && (
                        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-400">
                          <span className="text-sm font-medium">You Save: {formatCurrency(result.savings)}</span>
                        </div>
                      )}
                      {result.residency_status === 'nri' && (
                        <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                          <p className="text-xs text-amber-400">{result.residency_note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Old Regime */}
                  <div className={`glass rounded-2xl p-5 ${result.better_regime === "Old Regime" ? 'ring-2 ring-amber-500/50' : ''}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-slate-500" />
                      <h4 className="font-heading font-semibold text-white">Old Regime</h4>
                      {result.better_regime === "Old Regime" && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Better</span>
                      )}
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Gross Income</span>
                        <span className="text-white">{formatCurrency(result.old_regime.gross_income)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Total Deductions</span>
                        <span className="text-green-400">- {formatCurrency(result.old_regime.total_deductions)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-800 pt-2">
                        <span className="text-slate-400">Taxable Income</span>
                        <span className="text-white">{formatCurrency(result.old_regime.taxable_income)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Tax</span>
                        <span className="text-white">{formatCurrency(result.old_regime.tax_before_rebate)}</span>
                      </div>
                      {result.old_regime.rebate_87a > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Rebate 87A</span>
                          <span className="text-green-400">- {formatCurrency(result.old_regime.rebate_87a)}</span>
                        </div>
                      )}
                      {result.old_regime.surcharge > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Surcharge</span>
                          <span className="text-orange-400">+ {formatCurrency(result.old_regime.surcharge)}</span>
                        </div>
                      )}
                      {result.old_regime.marginal_relief > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Marginal Relief</span>
                          <span className="text-emerald-400">applied</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-400">Cess (4%)</span>
                        <span className="text-white">{formatCurrency(result.old_regime.cess)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-800 pt-2">
                        <span className="text-white font-medium">Total Tax</span>
                        <span className="text-amber-400 font-bold text-lg">{formatCurrency(result.old_regime.total_tax)}</span>
                      </div>
                    </div>
                  </div>

                  {/* New Regime */}
                  <div className={`glass rounded-2xl p-5 ${result.better_regime === "New Regime" ? 'ring-2 ring-cyan-500/50' : ''}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-cyan-500" />
                      <h4 className="font-heading font-semibold text-white">New Regime</h4>
                      {result.better_regime === "New Regime" && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Better</span>
                      )}
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Gross Income</span>
                        <span className="text-white">{formatCurrency(result.new_regime.gross_income)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Deductions (Std + NPS)</span>
                        <span className="text-green-400">- {formatCurrency(result.new_regime.total_deductions)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-800 pt-2">
                        <span className="text-slate-400">Taxable Income</span>
                        <span className="text-white">{formatCurrency(result.new_regime.taxable_income)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Tax</span>
                        <span className="text-white">{formatCurrency(result.new_regime.tax_before_rebate)}</span>
                      </div>
                      {result.new_regime.rebate_87a > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">
                            Rebate 87A {result.new_regime.marginal_relief > 0 ? '(Marginal Relief)' : ''}
                          </span>
                          <span className="text-green-400">- {formatCurrency(result.new_regime.rebate_87a)}</span>
                        </div>
                      )}
                      {result.new_regime.surcharge > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">
                            Surcharge {result.new_regime.marginal_relief > 0 && result.new_regime.rebate_87a === 0 ? '(after MR)' : ''}
                          </span>
                          <span className="text-orange-400">+ {formatCurrency(result.new_regime.surcharge)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-400">Cess (4%)</span>
                        <span className="text-white">{formatCurrency(result.new_regime.cess)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-800 pt-2">
                        <span className="text-white font-medium">Total Tax</span>
                        <span className="text-cyan-400 font-bold text-lg">{formatCurrency(result.new_regime.total_tax)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Deduction Breakdown */}
                <div className="glass rounded-2xl p-5">
                  <h4 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
                    <Info className="w-4 h-4 text-cyan-400" />
                    Your Deductions (Old Regime)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {result.deduction_details.hra_exemption > 0 && (
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-slate-400">HRA Exemption</p>
                        <p className="text-white font-medium">{formatCurrency(result.deduction_details.hra_exemption)}</p>
                      </div>
                    )}
                    {result.deduction_details.section_80c > 0 && (
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-slate-400">Section 80C</p>
                        <p className="text-white font-medium">{formatCurrency(result.deduction_details.section_80c)}</p>
                      </div>
                    )}
                    {result.deduction_details.section_80d > 0 && (
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-slate-400">Section 80D</p>
                        <p className="text-white font-medium">{formatCurrency(result.deduction_details.section_80d)}</p>
                      </div>
                    )}
                    {result.deduction_details.home_loan_interest > 0 && (
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-slate-400">Home Loan Interest</p>
                        <p className="text-white font-medium">{formatCurrency(result.deduction_details.home_loan_interest)}</p>
                      </div>
                    )}
                    {result.deduction_details.nps_80ccd > 0 && (
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-slate-400">NPS (80CCD)</p>
                        <p className="text-white font-medium">{formatCurrency(result.deduction_details.nps_80ccd)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center">
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/chat', { state: { initialQuery: `I have salary ${formatNumber(result.old_regime.gross_income)} with ${formatNumber(result.old_regime.total_deductions)} deductions. Any tax saving tips?` } })}
                    className="text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
                    data-testid="ask-ai-tips"
                  >
                    Get Tax Saving Tips from AI
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="glass rounded-2xl p-12 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-amber-500/20 flex items-center justify-center mx-auto mb-6">
                  <Calculator className="w-10 h-10 text-cyan-400" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-2">
                  Calculate Your Tax
                </h3>
                <p className="text-slate-400 max-w-md mx-auto mb-6">
                  Enter your income and deduction details to see which tax regime saves you more money.
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-sm">
                  <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400">7 Tax Slabs</span>
                  <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400">₹12L Zero Tax</span>
                  <span className="px-3 py-1 rounded-full bg-slate-700 text-slate-300">Instant Results</span>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Disclaimer */}
        <motion.div 
          className="mt-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm text-slate-500" data-testid="calc-disclaimer">
            This is an estimate. Consult a professional for accurate tax planning.
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-slate-800 mt-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            Calculations based on {fyInfo.act} for {fyInfo.label}.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CalculatorPage;
